import React, { useState, useEffect, useCallback } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { formatAddress, formatETH } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    History, 
    Download, 
    Filter, 
    Calendar,
    Wallet,
    Trophy,
    XCircle,
    Clock,
    ChevronLeft,
    ChevronRight,
    Search,
    ArrowUpRight,
    ArrowDownRight,
    RefreshCw
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
    open: 'bg-blue-500/20 text-blue-400',
    active: 'bg-yellow-500/20 text-yellow-400',
    resolved: 'bg-[#00FF66]/20 text-[#00FF66]',
    cancelled: 'bg-white/10 text-white/50',
    in_assertion: 'bg-[#9B6FE8]/20 text-[#9B6FE8]'
};

const outcomeLabels = {
    creator_wins: { label: 'Creator Won', color: 'text-[#00FF66]' },
    taker_wins: { label: 'Taker Won', color: 'text-[#00FF66]' },
    draw: { label: 'Draw', color: 'text-yellow-400' },
    cancelled: { label: 'Cancelled', color: 'text-white/50' },
    pending: { label: 'Pending', color: 'text-white/50' }
};

export function HistoryPage() {
    const { t } = useTranslation();
    const { address, isConnected, connect } = useWeb3();
    const [bets, setBets] = useState([]);
    const [filteredBets, setFilteredBets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    
    // Filters
    const [statusFilter, setStatusFilter] = useState('all');
    const [roleFilter, setRoleFilter] = useState('all');
    const [outcomeFilter, setOutcomeFilter] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [minAmount, setMinAmount] = useState('');
    const [maxAmount, setMaxAmount] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Fetch user's bet history
    const fetchHistory = useCallback(async () => {
        if (!address) {
            setLoading(false);
            return;
        }
        
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/users/${address}/history?limit=500`);
            if (response.ok) {
                const data = await response.json();
                setBets(data);
            }
        } catch (error) {
            console.error('Failed to fetch history:', error);
        } finally {
            setLoading(false);
        }
    }, [address]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    // Apply filters
    useEffect(() => {
        let result = [...bets];
        
        // Status filter
        if (statusFilter !== 'all') {
            result = result.filter(bet => bet.status === statusFilter);
        }
        
        // Role filter
        if (roleFilter !== 'all') {
            result = result.filter(bet => {
                const isCreator = bet.creator_address?.toLowerCase() === address?.toLowerCase();
                return roleFilter === 'created' ? isCreator : !isCreator;
            });
        }
        
        // Outcome filter
        if (outcomeFilter !== 'all') {
            result = result.filter(bet => bet.outcome === outcomeFilter);
        }
        
        // Date filters
        if (dateFrom) {
            const fromDate = new Date(dateFrom);
            result = result.filter(bet => new Date(bet.created_at) >= fromDate);
        }
        if (dateTo) {
            const toDate = new Date(dateTo);
            toDate.setHours(23, 59, 59);
            result = result.filter(bet => new Date(bet.created_at) <= toDate);
        }
        
        // Amount filters
        if (minAmount) {
            result = result.filter(bet => {
                const stake = bet.creator_address?.toLowerCase() === address?.toLowerCase() 
                    ? bet.creator_stake 
                    : bet.taker_stake;
                return stake >= parseFloat(minAmount);
            });
        }
        if (maxAmount) {
            result = result.filter(bet => {
                const stake = bet.creator_address?.toLowerCase() === address?.toLowerCase() 
                    ? bet.creator_stake 
                    : bet.taker_stake;
                return stake <= parseFloat(maxAmount);
            });
        }
        
        // Search query
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(bet => 
                bet.description?.toLowerCase().includes(query) ||
                bet.id?.toLowerCase().includes(query)
            );
        }
        
        setFilteredBets(result);
        setCurrentPage(1);
    }, [bets, statusFilter, roleFilter, outcomeFilter, dateFrom, dateTo, minAmount, maxAmount, searchQuery, address]);

    // Pagination
    const totalPages = Math.ceil(filteredBets.length / itemsPerPage);
    const paginatedBets = filteredBets.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Export to CSV
    const exportToCSV = async () => {
        setExporting(true);
        
        try {
            const headers = [
                'ID', 'Date', 'Description', 'Role', 'Status', 'Outcome',
                'Your Stake (ETH)', 'Total Pool (ETH)', 'Payout (ETH)', 'Profit/Loss (ETH)'
            ];
            
            const rows = filteredBets.map(bet => {
                const isCreator = bet.creator_address?.toLowerCase() === address?.toLowerCase();
                const stake = isCreator ? bet.creator_stake : bet.taker_stake;
                const totalPool = (bet.creator_stake || 0) + (bet.taker_stake || 0);
                const payout = bet.payout_amount || 0;
                const isWinner = bet.winner_address?.toLowerCase() === address?.toLowerCase();
                const profit = bet.status === 'resolved' 
                    ? (isWinner ? payout - stake : -stake)
                    : 0;
                
                return [
                    bet.id,
                    new Date(bet.created_at).toISOString().split('T')[0],
                    `"${bet.description?.replace(/"/g, '""') || ''}"`,
                    isCreator ? 'Creator' : 'Taker',
                    bet.status,
                    bet.outcome || 'pending',
                    stake?.toFixed(6) || '0',
                    totalPool?.toFixed(6) || '0',
                    payout?.toFixed(6) || '0',
                    profit?.toFixed(6) || '0'
                ];
            });
            
            const csvContent = [
                headers.join(','),
                ...rows.map(row => row.join(','))
            ].join('\n');
            
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `bet4peer_history_${address?.slice(0, 8)}_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            setExporting(false);
        }
    };

    // Clear all filters
    const clearFilters = () => {
        setStatusFilter('all');
        setRoleFilter('all');
        setOutcomeFilter('all');
        setDateFrom('');
        setDateTo('');
        setMinAmount('');
        setMaxAmount('');
        setSearchQuery('');
    };

    if (!isConnected) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-24">
                <div className="p-8 md:p-16 rounded-2xl bg-white/[0.02] border border-white/5 text-center max-w-lg mx-auto">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#00FF66]/10 flex items-center justify-center mx-auto mb-6">
                        <History className="w-8 h-8 md:w-10 md:h-10 text-[#00FF66]" />
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-white mb-4">{t('history.connectTitle')}</h2>
                    <p className="text-white/50 mb-8 text-sm md:text-base">
                        {t('history.connectDesc')}
                    </p>
                    <Button 
                        onClick={connect}
                        className="bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52] font-semibold px-6 py-4 md:px-8 md:py-6 text-base md:text-lg"
                        data-testid="history-connect-wallet"
                    >
                        <Wallet className="w-5 h-5 mr-2" />
                        {t('common.connectWallet')}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="history-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 md:mb-8">
                <div>
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2 text-white">
                        <span className="text-[#00FF66]">{t('history.headerHighlight')}</span> {t('history.headerTitle')}
                    </h1>
                    <p className="text-white/50 text-sm md:text-base">
                        {t('history.headerSubtitle')}
                    </p>
                </div>
                <div className="flex gap-2 md:gap-3">
                    <Button
                        variant="outline"
                        className="border-white/10 text-white hover:bg-white/5 text-sm md:text-base"
                        onClick={fetchHistory}
                        disabled={loading}
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        <span className="hidden sm:inline">{t('history.refresh')}</span>
                    </Button>
                    <Button
                        className="bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52] text-sm md:text-base"
                        onClick={exportToCSV}
                        disabled={exporting || filteredBets.length === 0}
                    >
                        <Download className={`w-4 h-4 mr-2 ${exporting ? 'animate-pulse' : ''}`} />
                        {t('history.exportCsv')}
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <Card className="bg-white/[0.02] border-white/5 mb-6">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base md:text-lg text-white flex items-center gap-2">
                        <Filter className="w-4 h-4 md:w-5 md:h-5 text-[#00FF66]" />
                        {t('history.filters')}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                        {/* Search */}
                        <div className="sm:col-span-2 lg:col-span-4">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                                <Input
                                    placeholder={t('history.searchPlaceholder')}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm"
                                />
                            </div>
                        </div>
                        
                        {/* Status */}
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm">
                                <SelectValue placeholder={t('history.statusLabel')} />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1a0a30] border-white/10">
                                <SelectItem value="all">{t('history.statusAll')}</SelectItem>
                                <SelectItem value="open">{t('history.statusOpen')}</SelectItem>
                                <SelectItem value="active">{t('history.statusActive')}</SelectItem>
                                <SelectItem value="resolved">{t('history.statusResolved')}</SelectItem>
                                <SelectItem value="cancelled">{t('history.statusCancelled')}</SelectItem>
                                <SelectItem value="in_assertion">{t('history.statusInAssertion')}</SelectItem>
                            </SelectContent>
                        </Select>
                        
                        {/* Role */}
                        <Select value={roleFilter} onValueChange={setRoleFilter}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm">
                                <SelectValue placeholder={t('history.roleLabel')} />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1a0a30] border-white/10">
                                <SelectItem value="all">{t('history.roleAll')}</SelectItem>
                                <SelectItem value="created">{t('history.roleCreated')}</SelectItem>
                                <SelectItem value="taken">{t('history.roleTaken')}</SelectItem>
                            </SelectContent>
                        </Select>
                        
                        {/* Outcome */}
                        <Select value={outcomeFilter} onValueChange={setOutcomeFilter}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm">
                                <SelectValue placeholder={t('history.outcomeLabel')} />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1a0a30] border-white/10">
                                <SelectItem value="all">{t('history.outcomeAll')}</SelectItem>
                                <SelectItem value="creator_wins">{t('history.outcomeCreatorWins')}</SelectItem>
                                <SelectItem value="taker_wins">{t('history.outcomeTakerWins')}</SelectItem>
                                <SelectItem value="draw">{t('history.outcomeDraw')}</SelectItem>
                                <SelectItem value="cancelled">{t('history.outcomeCancelled')}</SelectItem>
                            </SelectContent>
                        </Select>
                        
                        {/* Date From */}
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
                            <Input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                className="pl-10 bg-white/5 border-white/10 text-white text-sm [color-scheme:dark]"
                                placeholder={t('history.fromDate')}
                            />
                        </div>
                        
                        {/* Date To */}
                        <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
                            <Input
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                className="pl-10 bg-white/5 border-white/10 text-white text-sm [color-scheme:dark]"
                                placeholder={t('history.toDate')}
                            />
                        </div>
                        
                        {/* Min Amount */}
                        <Input
                            type="number"
                            step="0.001"
                            placeholder={t('history.minEth')}
                            value={minAmount}
                            onChange={(e) => setMinAmount(e.target.value)}
                            className="bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm"
                        />
                        
                        {/* Max Amount */}
                        <Input
                            type="number"
                            step="0.001"
                            placeholder={t('history.maxEth')}
                            value={maxAmount}
                            onChange={(e) => setMaxAmount(e.target.value)}
                            className="bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm"
                        />
                    </div>
                    
                    {/* Clear Filters */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                        <span className="text-sm text-white/50">
                            {t('history.betsCounter', { shown: filteredBets.length, total: bets.length })}
                        </span>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={clearFilters}
                            className="text-white/50 hover:text-white text-xs md:text-sm"
                        >
                            {t('history.clearAll')}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Results */}
            {loading ? (
                <div className="space-y-4">
                    {Array.from({ length: 5 }, (_, i) => `skel-${i}`).map((skKey) => (
                        <Skeleton key={skKey} className="h-20 md:h-24 bg-white/5" />
                    ))}
                </div>
            ) : filteredBets.length === 0 ? (
                <div className="p-8 md:p-16 rounded-2xl bg-white/[0.02] border border-white/5 text-center">
                    <History className="w-10 h-10 md:w-12 md:h-12 text-white/20 mx-auto mb-4" />
                    <h3 className="text-lg md:text-xl font-semibold text-white mb-2">No Bets Found</h3>
                    <p className="text-white/50 text-sm md:text-base">
                        {bets.length === 0 
                            ? "You haven't placed any bets yet." 
                            : "No bets match your current filters."}
                    </p>
                </div>
            ) : (
                <>
                    {/* Bet List */}
                    <div className="space-y-3">
                        {paginatedBets.map((bet) => {
                            const isCreator = bet.creator_address?.toLowerCase() === address?.toLowerCase();
                            const stake = isCreator ? bet.creator_stake : bet.taker_stake;
                            const isWinner = bet.winner_address?.toLowerCase() === address?.toLowerCase();
                            const profit = bet.status === 'resolved' && bet.payout_amount
                                ? (isWinner ? bet.payout_amount - stake : -stake)
                                : null;
                            
                            return (
                                <Link key={bet.id} to={`/bets/${bet.id}`}>
                                    <Card className="bg-white/[0.02] border-white/5 hover:border-[#00FF66]/30 transition-colors cursor-pointer">
                                        <CardContent className="p-3 md:p-4">
                                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
                                                {/* Left - Role & Description */}
                                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                                    <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                                        isCreator ? 'bg-[#00FF66]/20' : 'bg-[#6F3FCA]/20'
                                                    }`}>
                                                        {isCreator ? (
                                                            <ArrowUpRight className="w-4 h-4 md:w-5 md:h-5 text-[#00FF66]" />
                                                        ) : (
                                                            <ArrowDownRight className="w-4 h-4 md:w-5 md:h-5 text-[#9B6FE8]" />
                                                        )}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-white font-medium truncate text-sm md:text-base">
                                                            {bet.description}
                                                        </p>
                                                        <div className="flex flex-wrap items-center gap-2 mt-1">
                                                            <Badge className={`text-xs ${statusColors[bet.status] || 'bg-white/10 text-white/50'}`}>
                                                                {bet.status}
                                                            </Badge>
                                                            <span className="text-xs text-white/40">
                                                                {new Date(bet.created_at).toLocaleDateString()}
                                                            </span>
                                                            <span className="text-xs text-white/30 hidden sm:inline">
                                                                {isCreator ? 'Created' : 'Taken'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {/* Right - Stakes & Profit */}
                                                <div className="flex items-center gap-4 md:gap-6 pl-11 md:pl-0">
                                                    <div className="text-right">
                                                        <div className="text-white font-mono text-sm md:text-base">{stake?.toFixed(4)} ETH</div>
                                                        <div className="text-white/40 text-xs">Your stake</div>
                                                    </div>
                                                    
                                                    {bet.status === 'resolved' && profit !== null && (
                                                        <div className="text-right min-w-[80px]">
                                                            <div className={`font-mono text-sm md:text-base ${profit >= 0 ? 'text-[#00FF66]' : 'text-red-400'}`}>
                                                                {profit >= 0 ? '+' : ''}{profit.toFixed(4)}
                                                            </div>
                                                            <div className="text-white/40 text-xs">
                                                                {isWinner ? 'Won' : 'Lost'}
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {bet.status === 'resolved' && (
                                                        <div className="hidden md:block">
                                                            {isWinner ? (
                                                                <Trophy className="w-5 h-5 text-[#FFD700]" />
                                                            ) : (
                                                                <XCircle className="w-5 h-5 text-red-400" />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </Link>
                            );
                        })}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-6 pt-6 border-t border-white/5">
                            <span className="text-sm text-white/50">
                                Page {currentPage} of {totalPages}
                            </span>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="border-white/10 text-white"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="border-white/10 text-white"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
